import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { EnrollmentResult } from '@/types/enrollment';
import RhythmPrintViz from '@/components/RhythmPrintViz';
import StatusBadge from '@/components/StatusBadge';

/**
 * ProfileCreated — Success page shown after enrollment finalization.
 * Displays the RhythmPrint visualization and links to test login.
 */
export default function ProfileCreated() {
  const navigate = useNavigate();
  const location = useLocation();
  const result = (location.state as { result?: EnrollmentResult })?.result;

  // Generate mock embedding if none provided
  const embedding = useMemo(() => {
    if (result?.rhythmPrint && result.rhythmPrint.length > 0) {
      return result.rhythmPrint;
    }
    return Array.from({ length: 64 }, () => Math.random() * 2 - 1);
  }, [result]);

  const profileHealth = result?.profileHealth ?? 85;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 py-12">
      {/* Success animation */}
      <div className="text-center mb-8 animate-fade-in">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-accent-success/15 mb-4">
          <span className="text-3xl text-accent-success">{'\u2713'}</span>
        </div>
        <h1 className="text-2xl md:text-3xl font-bold text-txt-primary font-sans mb-2">
          Perfil Biometrico Criado!
        </h1>
        <p className="text-sm text-txt-muted font-sans max-w-md">
          Sua assinatura de digitacao foi registrada com sucesso.
          Abaixo esta a visualizacao unica do seu RhythmPrint.
        </p>
      </div>

      {/* RhythmPrint */}
      <div className="mb-8 animate-slide-up">
        <RhythmPrintViz
          embedding={embedding}
          size={300}
          color="#7c5cfc"
          secondaryColor="#4fc3f7"
          showLabels={true}
          animate={true}
        />
      </div>

      {/* Profile info */}
      <div className="text-center mb-8 space-y-3 animate-fade-in">
        <div className="flex items-center justify-center gap-2">
          <StatusBadge label="Perfil Ativo" variant="success" pulse />
        </div>

        <div className="flex items-center justify-center gap-6 text-sm font-sans">
          <div className="text-center">
            <span className="block text-2xl font-bold font-mono text-accent-brand">
              {Math.round(profileHealth)}%
            </span>
            <span className="text-xs text-txt-muted">Saude do Perfil</span>
          </div>
          <div className="w-px h-8 bg-bg-surface" />
          <div className="text-center">
            <span className="block text-2xl font-bold font-mono text-accent-info">
              {embedding.length}
            </span>
            <span className="text-xs text-txt-muted">Camadas</span>
          </div>
          <div className="w-px h-8 bg-bg-surface" />
          <div className="text-center">
            <span className="block text-2xl font-bold font-mono text-accent-success">
              5
            </span>
            <span className="text-xs text-txt-muted">Sessoes</span>
          </div>
        </div>
      </div>

      {/* Explanation */}
      <div className="max-w-md w-full p-4 bg-bg-secondary border border-bg-surface rounded-card mb-8 animate-fade-in">
        <h3 className="text-sm font-medium text-txt-primary font-sans mb-2">
          O que e o RhythmPrint?
        </h3>
        <p className="text-xs text-txt-muted font-sans leading-relaxed">
          O RhythmPrint e uma representacao visual da sua identidade digital.
          Cada anel representa uma camada diferente do seu perfil biometrico,
          capturado pela nossa IA a partir da sua forma unica de digitar.
          Este padrao e exclusivamente seu.
        </p>
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row items-center gap-4 animate-slide-up">
        <button
          onClick={() => navigate('/login')}
          className="bg-accent-brand hover:bg-accent-brand/90 text-white px-8 py-3.5 rounded-xl font-sans font-semibold text-base transition-all hover:shadow-lg hover:shadow-accent-brand/20"
        >
          Testar meu login
        </button>
        <button
          onClick={() => navigate('/dashboard')}
          className="border border-bg-surface hover:border-accent-brand/50 text-txt-primary px-8 py-3.5 rounded-xl font-sans font-medium text-base transition-all"
        >
          Ir para o Dashboard
        </button>
      </div>
    </div>
  );
}
